import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Globe, Save, LogOut, Plus, Home, Briefcase, MoreHorizontal, Edit, Trash2, CheckCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../lib/translations';
import { toast } from 'sonner';
import axios from 'axios';
import { authAxios } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, language, updateProfile, logout, isAuthenticated, token } = useAuth();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    language: user?.language || 'en'
  });
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(true);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    house_flat_no: '',
    building_apt_name: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    address_type: 'Home',
    is_primary: false
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchAddresses();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  const fetchAddresses = async () => {
    try {
      const response = await authAxios.get(`${API}/auth/addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data);
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateProfile(formData);
      toast.success(language === 'hi' ? 'प्रोफ़ाइल अपडेट की गई!' : 'Profile updated!');
    } catch (error) {
      toast.error(language === 'hi' ? 'अपडेट विफल' : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openAddressDialog = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressForm({
        house_flat_no: address.house_flat_no,
        building_apt_name: address.building_apt_name || '',
        landmark: address.landmark || '',
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        address_type: address.address_type,
        is_primary: address.is_primary
      });
    } else {
      setEditingAddress(null);
      setAddressForm({
        house_flat_no: '',
        building_apt_name: '',
        landmark: '',
        city: '',
        state: '',
        pincode: '',
        address_type: 'Home',
        is_primary: addresses.length === 0
      });
    }
    setShowAddressDialog(true);
  };

  const handleAddressChange = (e) => {
    setAddressForm({ ...addressForm, [e.target.name]: e.target.value });
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (addressForm.pincode.length !== 6 || !/^\d+$/.test(addressForm.pincode)) {
      toast.error(language === 'hi' ? 'कृपया वैध 6 अंकों का पिनकोड दर्ज करें' : 'Please enter a valid 6-digit pincode');
      return;
    }

    setLoading(true);

    try {
      if (editingAddress) {
        await authAxios.put(`${API}/auth/addresses/${editingAddress.id}`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'hi' ? 'पता अपडेट किया गया!' : 'Address updated!');
      } else {
        await authAxios.post(`${API}/auth/addresses`, addressForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(language === 'hi' ? 'पता जोड़ा गया!' : 'Address added!');
      }
      fetchAddresses();
      setShowAddressDialog(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (language === 'hi' ? 'पता सहेजने में त्रुटि' : 'Failed to save address');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!confirm(language === 'hi' ? 'क्या आप वाकई इस पते को हटाना चाहते हैं?' : 'Are you sure you want to delete this address?')) {
      return;
    }

    try {
      await authAxios.delete(`${API}/auth/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'hi' ? 'पता हटाया गया!' : 'Address deleted!');
      fetchAddresses();
    } catch (error) {
      toast.error(language === 'hi' ? 'त्रुटि' : 'Error');
    }
  };

  const handleSetPrimary = async (addressId) => {
    try {
      await authAxios.put(`${API}/auth/addresses/${addressId}/set-primary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'hi' ? 'मुख्य पता अपडेट किया गया!' : 'Primary address updated!');
      fetchAddresses();
    } catch (error) {
      toast.error(language === 'hi' ? 'त्रुटि' : 'Error');
    }
  };

  const getAddressTypeIcon = (type) => {
    switch (type) {
      case 'Home':
        return <Home className="w-4 h-4" />;
      case 'Work':
        return <Briefcase className="w-4 h-4" />;
      default:
        return <MoreHorizontal className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="profile-page">
      <div className="container-app max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('profile', language)}</h1>
        
        {/* Profile Information */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-green-100">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  {t('name', language)}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1"
                  data-testid="name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {t('phone', language)}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                {t('email', language)}
              </Label>
              <Input
                id="email"
                value={user?.email}
                disabled
                className="mt-1 bg-slate-50"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="language" className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-400" />
                {t('language', language)}
              </Label>
              <Select name="language" value={formData.language} onValueChange={(value) => setFormData({ ...formData, language: value })}>
                <SelectTrigger className="mt-1" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">हिन्दी</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={loading} className="flex-1" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {loading ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') : t('save', language)}
              </Button>
              <Button type="button" variant="outline" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout', language)}
              </Button>
            </div>
            
            {/* Change Password Link */}
            <div className="pt-4 border-t border-slate-200">
              <Link to="/forgot-password" className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium">
                <Lock className="w-4 h-4" />
                {language === 'hi' ? 'पासवर्ड बदलें' : 'Change Password'}
              </Link>
            </div>
          </form>
        </div>

        {/* Addresses Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">
              {language === 'hi' ? 'सहेजे गए पते' : 'Saved Addresses'}
            </h2>
            <Button onClick={() => openAddressDialog()} size="sm" data-testid="add-address-btn">
              <Plus className="w-4 h-4 mr-2" />
              {language === 'hi' ? 'पता जोड़ें' : 'Add Address'}
            </Button>
          </div>

          {addressLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>{language === 'hi' ? 'कोई पता नहीं मिला' : 'No addresses found'}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {addresses.map((address) => (
                <div
                  key={address.id}
                  className={`border rounded-lg p-4 relative ${address.is_primary ? 'border-green-500 bg-green-50/50' : 'border-slate-200'}`}
                  data-testid={`address-card-${address.id}`}
                >
                  {address.is_primary && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        {language === 'hi' ? 'मुख्य' : 'Primary'}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                      {getAddressTypeIcon(address.address_type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900 mb-1">{address.address_type}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {address.house_flat_no}
                        {address.building_apt_name && `, ${address.building_apt_name}`}
                        {address.landmark && `, ${address.landmark}`}
                        <br />
                        {address.city}, {address.state} - {address.pincode}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 border-t">
                    {!address.is_primary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetPrimary(address.id)}
                        className="flex-1"
                      >
                        {language === 'hi' ? 'मुख्य बनाएं' : 'Set as Primary'}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddressDialog(address)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAddress(address.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Address Dialog */}
      <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAddress
                ? (language === 'hi' ? 'पता संपादित करें' : 'Edit Address')
                : (language === 'hi' ? 'नया पता जोड़ें' : 'Add New Address')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddressSubmit} className="space-y-4">
            <div>
              <Label htmlFor="house_flat_no">{language === 'hi' ? 'घर/फ्लैट नंबर *' : 'House/Flat No. *'}</Label>
              <Input
                id="house_flat_no"
                name="house_flat_no"
                value={addressForm.house_flat_no}
                onChange={handleAddressChange}
                required
                className="mt-1"
                placeholder="e.g., 123, Flat 4B"
              />
            </div>

            <div>
              <Label htmlFor="building_apt_name">{language === 'hi' ? 'बिल्डिंग/अपार्टमेंट का नाम' : 'Building/Apartment Name'}</Label>
              <Input
                id="building_apt_name"
                name="building_apt_name"
                value={addressForm.building_apt_name}
                onChange={handleAddressChange}
                className="mt-1"
                placeholder="e.g., Green View Apartments"
              />
            </div>

            <div>
              <Label htmlFor="landmark">{language === 'hi' ? 'लैंडमार्क' : 'Landmark'}</Label>
              <Input
                id="landmark"
                name="landmark"
                value={addressForm.landmark}
                onChange={handleAddressChange}
                className="mt-1"
                placeholder="e.g., Near City Hospital"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">{language === 'hi' ? 'शहर *' : 'City *'}</Label>
                <Input
                  id="city"
                  name="city"
                  value={addressForm.city}
                  onChange={handleAddressChange}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="state">{language === 'hi' ? 'राज्य *' : 'State *'}</Label>
                <Input
                  id="state"
                  name="state"
                  value={addressForm.state}
                  onChange={handleAddressChange}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pincode">{language === 'hi' ? 'पिनकोड *' : 'Pincode *'}</Label>
              <Input
                id="pincode"
                name="pincode"
                value={addressForm.pincode}
                onChange={handleAddressChange}
                required
                className="mt-1"
                pattern="[0-9]{6}"
                maxLength="6"
              />
            </div>

            <div>
              <Label htmlFor="address_type">{language === 'hi' ? 'पते का प्रकार' : 'Address Type'}</Label>
              <Select
                value={addressForm.address_type}
                onValueChange={(value) => setAddressForm({ ...addressForm, address_type: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Home">{language === 'hi' ? 'घर' : 'Home'}</SelectItem>
                  <SelectItem value="Work">{language === 'hi' ? 'कार्यालय' : 'Work'}</SelectItem>
                  <SelectItem value="Other">{language === 'hi' ? 'अन्य' : 'Other'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={addressForm.is_primary}
                onChange={(e) => setAddressForm({ ...addressForm, is_primary: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
              />
              <Label htmlFor="is_primary" className="cursor-pointer">
                {language === 'hi' ? 'मुख्य पते के रूप में सेट करें' : 'Set as primary address'}
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddressDialog(false)}>
                {language === 'hi' ? 'रद्द करें' : 'Cancel'}
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') : (language === 'hi' ? 'सेव करें' : 'Save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
